import React, { Suspense, lazy } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { ProtectedRoute } from '../components/auth/ProtectedRoute'

// Lazy load halaman
const LandingPage = lazy(() => import('../pages/LandingPage'))
const LoginPage = lazy(() => import('../pages/LoginPage'))
const DashboardPage = lazy(() => import('../pages/DashboardPage'))
const ClassesPage = lazy(() => import('../pages/ClassesPage'))
const StudentsPage = lazy(() => import('../pages/StudentsPage'))
const PlanningPage = lazy(() => import('../pages/PlanningPage'))
const JournalPage = lazy(() => import('../pages/JournalPage'))
const AssessmentPage = lazy(() => import('../pages/AssessmentPage'))
const GradingPage = lazy(() => import('../pages/GradingPage'))
const AnalysisPage = lazy(() => import('../pages/AnalysisPage'))
const ReflectionPage = lazy(() => import('../pages/ReflectionPage'))
const FollowUpPage = lazy(() => import('../pages/FollowUpPage'))
const RecapPage = lazy(() => import('../pages/RecapPage'))
const SchedulePage = lazy(() => import('../pages/SchedulePage'))
const SettingsPage = lazy(() => import('../pages/SettingsPage'))

// Halaman murid
const StudentCodePage = lazy(() => import('../pages/student/StudentCodePage'))
const StudentTaskPage = lazy(() => import('../pages/student/StudentTaskPage'))
const StudentResultsPage = lazy(() => import('../pages/student/StudentResultsPage'))

// Halaman admin/superadmin
const AdminDashboardPage = lazy(() => import('../pages/admin/AdminDashboardPage'))
const AdminTeachersPage = lazy(() => import('../pages/admin/AdminTeachersPage'))
const AdminQuotaPage = lazy(() => import('../pages/admin/AdminQuotaPage'))
const AdminCurriculumPage = lazy(() => import('../pages/admin/AdminCurriculumPage'))
const AdminAuditLogPage = lazy(() => import('../pages/admin/AdminAuditLogPage'))

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-ivory">
      <div className="w-8 h-8 border-4 border-cyan-600 border-t-transparent rounded-full animate-spin" />
    </div>
  )
}

function NotAuthorized() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-ivory">
      <div className="text-5xl">🔒</div>
      <h1 className="text-xl font-bold text-slate-700">Akses Ditolak</h1>
      <p className="text-slate-500 text-sm">Anda tidak memiliki izin untuk mengakses halaman ini.</p>
      <a href="/dashboard" className="text-cyan-600 text-sm font-medium hover:underline">
        Kembali ke Dashboard
      </a>
    </div>
  )
}

export function AppRouter() {
  return (
    <BrowserRouter>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Publik */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/landing" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/not-authorized" element={<NotAuthorized />} />

          {/* Murid — akses via kode (tidak perlu auth Firebase biasa) */}
          <Route path="/m" element={<StudentCodePage />} />
          <Route path="/m/:code" element={<StudentCodePage />} />
          <Route path="/tugas/:assignmentId" element={<StudentTaskPage />} />
          <Route path="/hasil/:assignmentId" element={<StudentResultsPage />} />

          {/* === AREA GURU === */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute allowedRoles={['teacher']}>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/kelas"
            element={
              <ProtectedRoute allowedRoles={['teacher']}>
                <ClassesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/kelas/:classId/murid"
            element={
              <ProtectedRoute allowedRoles={['teacher']}>
                <StudentsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/perencanaan"
            element={
              <ProtectedRoute allowedRoles={['teacher']}>
                <PlanningPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/jadwal"
            element={
              <ProtectedRoute allowedRoles={['teacher']}>
                <SchedulePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/jurnal"
            element={
              <ProtectedRoute allowedRoles={['teacher']}>
                <JournalPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/asesmen"
            element={
              <ProtectedRoute allowedRoles={['teacher']}>
                <AssessmentPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/penilaian"
            element={
              <ProtectedRoute allowedRoles={['teacher']}>
                <GradingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/analisis"
            element={
              <ProtectedRoute allowedRoles={['teacher']}>
                <AnalysisPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/refleksi"
            element={
              <ProtectedRoute allowedRoles={['teacher']}>
                <ReflectionPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tindak-lanjut"
            element={
              <ProtectedRoute allowedRoles={['teacher']}>
                <FollowUpPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/rekap"
            element={
              <ProtectedRoute allowedRoles={['teacher']}>
                <RecapPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pengaturan"
            element={
              <ProtectedRoute allowedRoles={['teacher']}>
                <SettingsPage />
              </ProtectedRoute>
            }
          />

          {/* === AREA SUPERADMIN === */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute allowedRoles={['superadmin']}>
                <AdminDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/guru"
            element={
              <ProtectedRoute allowedRoles={['superadmin']}>
                <AdminTeachersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/kuota"
            element={
              <ProtectedRoute allowedRoles={['superadmin']}>
                <AdminQuotaPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/kurikulum"
            element={
              <ProtectedRoute allowedRoles={['superadmin']}>
                <AdminCurriculumPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/audit-log"
            element={
              <ProtectedRoute allowedRoles={['superadmin']}>
                <AdminAuditLogPage />
              </ProtectedRoute>
            }
          />

          {/* Redirect default */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  )
}
